import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createForm, updateForm, fetchForm } from '@/store/slices/formSlice';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { LoadingSpinner } from '@/components/ui/loading-spinner'

const FormBuilder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentForm, loading, error } = useSelector(state => state.forms);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    terms_content: '',
    is_active: true,
    requires_verification: false
  });

  const [fields, setFields] = useState([]);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (id) {
      setIsEditing(true);
      dispatch(fetchForm(id));
    }
  }, [id, dispatch]);

  const parseOptions = (options) => {
    if (Array.isArray(options)) return options;
    if (typeof options === 'string') {
      try {
        return JSON.parse(options);
      } catch (e) {
        console.error('Failed to parse options:', e);
        return [];
      }
    }
    return [];
  };

  // Then update your useEffect:
  useEffect(() => {
    if (isEditing && currentForm) {
      setFormData({
        name: currentForm.name,
        description: currentForm.description,
        terms_content: currentForm.terms_content,
        is_active: currentForm.is_active,
        requires_verification: currentForm.requires_verification
      });

      const parsedFields = currentForm.fields?.map(field => ({
        ...field,
        options: parseOptions(field.options)
      })) || [];

      setFields(parsedFields);
    }
  }, [currentForm, isEditing]);

  const addField = () => {
    setFields([...fields, {
      id: `field_${Date.now()}`,
      label: '',
      type: 'text',
      required: false,
      placeholder: '',
      description: '',
      options: [],
      validation_rules: {},
      order: fields.length
    }]);
  };

  const updateField = (index, updates) => {
    const newFields = [...fields];
    newFields[index] = { ...newFields[index], ...updates };
    setFields(newFields);
  };

  const removeField = (index) => {
    setFields(fields.filter((_, i) => i !== index));
  };

  const addOptionToField = (fieldIndex) => {
    const newFields = [...fields];
    const currentOptions = newFields[fieldIndex].options;

    // Ensure options is always an array
    if (typeof currentOptions === 'string') {
      try {
        newFields[fieldIndex].options = JSON.parse(currentOptions);
      } catch (e) {
        newFields[fieldIndex].options = [];
      }
    } else if (!Array.isArray(currentOptions)) {
      newFields[fieldIndex].options = [];
    }

    newFields[fieldIndex].options.push({
      value: '',
      label: ''
    });
    setFields(newFields);
  };

  const updateFieldOption = (fieldIndex, optionIndex, updates) => {
    const newFields = [...fields];
    newFields[fieldIndex].options[optionIndex] = {
      ...newFields[fieldIndex].options[optionIndex],
      ...updates
    };
    setFields(newFields);
  };

  const removeFieldOption = (fieldIndex, optionIndex) => {
    const newFields = [...fields];
    newFields[fieldIndex].options = newFields[fieldIndex].options.filter((_, i) => i !== optionIndex);
    setFields(newFields);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formWithFields = {
      ...formData,
      fields: fields.map(field => ({
        ...field,
        options: field.type === 'select' ? field.options : undefined
      }))
    };

    if (isEditing) {
      await dispatch(updateForm({ formId: id, formData: formWithFields }));
    } else {
      await dispatch(createForm(formWithFields));
    }

    navigate('/admin/forms');
  };

  const fieldTypes = [
    { value: 'text', label: 'Text' },
    { value: 'email', label: 'Email' },
    { value: 'number', label: 'Number' },
    { value: 'date', label: 'Date' },
    { value: 'textarea', label: 'Text Area' },
    { value: 'select', label: 'Dropdown Select' },
    { value: 'checkbox', label: 'Checkbox' },
    { value: 'file', label: 'File Upload' },
  ];

  if (loading) {
    return <div className="flex justify-center py-8"><LoadingSpinner /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/forms')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-3xl font-bold">
          {isEditing ? 'Edit Form' : 'Create New Form'}
        </h1>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Form Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Form Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="Enter form name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Enter form description"
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="terms_content">Terms and Conditions *</Label>
              <Textarea
                id="terms_content"
                value={formData.terms_content}
                onChange={(e) => setFormData({ ...formData, terms_content: e.target.value })}
                placeholder="Enter terms and conditions content"
                rows={6}
                required
              />
            </div>

            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <Label htmlFor="is_active">Active Form</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="requires_verification"
                  checked={formData.requires_verification}
                  onCheckedChange={(checked) => setFormData({ ...formData, requires_verification: checked })}
                />
                <Label htmlFor="requires_verification">Requires Verification</Label>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Form Fields</CardTitle>
            <Button type="button" onClick={addField}>
              <Plus className="h-4 w-4 mr-2" />
              Add Field
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {fields.map((field, index) => (
              <div key={field.id} className="border rounded-lg p-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Field Label *</Label>
                    <Input
                      value={field.label}
                      onChange={(e) => updateField(index, { label: e.target.value })}
                      placeholder="Enter field label"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Field Type *</Label>
                    <Select
                      value={field.type}
                      onValueChange={(value) => updateField(index, { type: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select field type" />
                      </SelectTrigger>
                      <SelectContent>
                        {fieldTypes.map(type => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Placeholder</Label>
                    <Input
                      value={field.placeholder}
                      onChange={(e) => updateField(index, { placeholder: e.target.value })}
                      placeholder="Enter placeholder text"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Input
                      value={field.description}
                      onChange={(e) => updateField(index, { description: e.target.value })}
                      placeholder="Enter field description"
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id={`required-${field.id}`}
                      checked={field.required}
                      onChange={(e) => updateField(index, { required: e.target.checked })}
                      className="rounded border-gray-300"
                    />
                    <Label htmlFor={`required-${field.id}`}>Required Field</Label>
                  </div>
                </div>

                {/* Options for select fields */}
                {field.type === 'select' && (
                  <div className="space-y-3">
                    <Label>Dropdown Options</Label>
                    {parseOptions(field.options).map((option, optionIndex) => (
                      <div key={optionIndex} className="flex items-center space-x-2">
                        <Input
                          placeholder="Option value"
                          value={option.value}
                          onChange={(e) => updateFieldOption(index, optionIndex, { value: e.target.value })}
                          className="flex-1"
                        />
                        <Input
                          placeholder="Option label"
                          value={option.label}
                          onChange={(e) => updateFieldOption(index, optionIndex, { label: e.target.value })}
                          className="flex-1"
                        />
                        <Button
                          type="button"
                          variant="destructive"
                          size="icon"
                          onClick={() => removeFieldOption(index, optionIndex)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => addOptionToField(index)}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Option
                    </Button>
                  </div>
                )}

                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={() => removeField(index)}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Remove Field
                </Button>
              </div>
            ))}

            {fields.length === 0 && (
              <div className="text-center text-muted-foreground py-8">
                No fields added yet. Click "Add Field" to get started.
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex space-x-4">
          <Button type="submit" disabled={loading}>
            {loading ? 'Saving...' : (isEditing ? 'Update Form' : 'Create Form')}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate('/admin/forms')}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

export default FormBuilder;